import Button from 'modules/shared-components/button/button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { valueAsSafeNumber } from 'utils/FormatNumber';
import { useWalletsSearchFilters } from './usePageParams';

import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import {
  PresetButton,
  PresetButtonDescription,
  PresetButtonTitle,
} from '../TelegramCalls/PresetButton';
import NumberInput from '../../../components/inputs/number-input';
import CustomModal from '../../../components/modal/CustomModal';
import { chainAsset, Chains } from 'api/types/httpsTypes/d-wallets';

type Props = {
  handleClose: () => void;
  chain: Chains;
};
export const schema = yup.object({
  min: yup.number().min(0),
  max: yup.number().min(0),
});

type FormState = yup.InferType<typeof schema>;

const CHAIN_PRICES: Record<Chains, number> = {
  [Chains.Ethereum]: 4000,
  [Chains.BinanceChain]: 800,
  [Chains.Solana]: 250,
  [Chains.Base]: 4000,
};

export function PnlFilterModal({ handleClose, chain }: Props) {
  const { filtersState, setFilters } = useWalletsSearchFilters();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormState>({
    resolver: yupResolver(schema),
    defaultValues: {
      min: filtersState.minPnl,
      max: filtersState.maxPnl,
    },
  });

  const isChainAsset = filtersState.isChainAsset;
  const price = CHAIN_PRICES[chain];

  // helper: convert USD preset into current unit
  const convertValue = (usd?: number) =>
    usd !== undefined ? (isChainAsset ? usd / price : usd) : undefined;

  return (
    <CustomModal title="Realized PnL Range" handleClose={handleClose} showModal>
      <form
        autoComplete="off"
        className="contents"
        onSubmit={(e) => {
          void handleSubmit((form) => {
            setFilters({
              ...filtersState,
              minPnl: form.min || undefined,
              maxPnl: form.max || undefined,
            });
            handleClose();
          })(e);
        }}
      >
        <CustomModal.Body className="xxs:p-4">
          <CustomModal.Title className="hidden lg:flex">
            Realized Profit and Loss Range
          </CustomModal.Title>

          <CustomModal.Content>
            <div className="xxs:space-y-3">
              <p className="xxs:text-sm">
                The Realized PnL Range filter lets you focus on wallets with a
                specific amount of PnL over the last 30 days.
              </p>

              <div className="grid grid-cols-3 xxs:gap-1 xxs:mb-4">
                <PresetButton
                  onClick={() => {
                    setValue('min', convertValue(0));
                    setValue('max', convertValue(5000));
                  }}
                >
                  <PresetButtonTitle>👌 Medium</PresetButtonTitle>
                  <PresetButtonDescription>
                    {isChainAsset
                      ? `${(5000 / price).toFixed(0)} ${chainAsset(chain)}`
                      : '$5k'}
                  </PresetButtonDescription>
                </PresetButton>

                <PresetButton
                  onClick={() => {
                    setValue('min', convertValue(5000));
                    setValue('max', convertValue(50000));
                  }}
                >
                  <PresetButtonTitle>👍 Good</PresetButtonTitle>
                  <PresetButtonDescription>
                    {isChainAsset
                      ? `${(5000 / price).toFixed(0)}–${(50000 / price).toFixed(
                          0
                        )} ${chainAsset(chain)}`
                      : '$5k–$50k'}
                  </PresetButtonDescription>
                </PresetButton>

                <PresetButton
                  onClick={() => {
                    setValue('min', convertValue(50000));
                    setValue('max', undefined);
                  }}
                >
                  <PresetButtonTitle>🚀 High</PresetButtonTitle>
                  <PresetButtonDescription>
                    {isChainAsset
                      ? `>${(50000 / price).toFixed(0)} ${chainAsset(chain)}`
                      : '>$50k'}
                  </PresetButtonDescription>
                </PresetButton>
              </div>

              <p className="xxs:mb-1">Custom</p>
              <div className="flex xxs:gap-1">
                <NumberInput
                  label="Min"
                  prefix={!isChainAsset ? '$' : undefined}
                  suffix={isChainAsset ? chainAsset(chain) : undefined}
                  {...register('min', { setValueAs: valueAsSafeNumber })}
                  error={errors.min?.message}
                />
                <NumberInput
                  label="Max"
                  prefix={!isChainAsset ? '$' : undefined}
                  suffix={isChainAsset ? chainAsset(chain) : undefined}
                  {...register('max', { setValueAs: valueAsSafeNumber })}
                  error={errors.max?.message}
                />
              </div>
            </div>
          </CustomModal.Content>
        </CustomModal.Body>

        <CustomModal.Footer>
          <PrimaryButton type="submit" className="xxs:text-base lg:text-xs">
            SAVE
          </PrimaryButton>
          <Button
            className="xxs:text-base lg:text-xs"
            type="button"
            variant="dexNeutral"
            onClick={handleClose}
          >
            Close
          </Button>
        </CustomModal.Footer>
      </form>
    </CustomModal>
  );
}
